import type {
  InventoryItem,
  ItemCondition,
  ListingStatus,
  Marketplace,
  Money,
  Sale,
  SaleStatus,
} from "./types";
import type { SpreadsheetOptions, SpreadsheetSheet } from "./spreadsheetImport";

export const migrationFields = [
  ["ignore", "Do not use"],
  ["record_id", "Record / transaction ID"],
  ["item_id", "Listwork item ID"],
  ["sku", "SKU / stock code"],
  ["title", "Item title"],
  ["description", "Description"],
  ["status", "Item or transaction status"],
  ["condition", "Condition"],
  ["brand", "Brand"],
  ["size", "Size / variant"],
  ["category", "Category"],
  ["location", "Stock location"],
  ["notes", "Notes"],
  ["available", "Currently available quantity"],
  ["quantity", "Transaction quantity"],
  ["purchase_cost", "Purchase cost"],
  ["cost_units", "Units in purchase cost total"],
  ["acquired_at", "Purchase date"],
  ["target_price", "Target price"],
  ["currency", "Currency"],
  ["marketplace", "Sales channel"],
  ["seller", "Seller account ID"],
  ["listing_id", "Listing ID"],
  ["listing_url", "Listing URL"],
  ["listing_status", "Listing status"],
  ["listing_price", "Listing price"],
  ["listed_at", "Date listed"],
  ["order_id", "Order ID"],
  ["line_id", "Order line ID"],
  ["sold_at", "Sale date"],
  ["sale_price", "Sale amount (whole line)"],
  ["shipping_charged", "Postage charged to buyer"],
  ["fees", "Marketplace fees"],
  ["payment_fees", "Payment fees"],
  ["shipping_cost", "Postage cost"],
  ["packaging_cost", "Packaging cost"],
  ["refund", "Total refunded for this sale"],
  ["event_at", "Return / event date"],
  ["carrier", "Shipping carrier"],
  ["tracking", "Tracking number"],
  ["shipped_at", "Date shipped"],
  ["completed_at", "Date completed"],
] as const;
export type MigrationField = (typeof migrationFields)[number][0];
export type MigrationStatus =
  | "stock"
  | "listed"
  | "ended"
  | "archived"
  | "reserved"
  | "unpaid"
  | "paid"
  | "packed"
  | "shipped"
  | "completed"
  | "cancelled"
  | "refunded"
  | "partial_refund"
  | "returned"
  | "exchanged"
  | "lost"
  | "damaged"
  | "donated"
  | "personal_use"
  | "written_off";
export const migrationStatuses: Array<[MigrationStatus, string]> = [
  ["stock", "In stock"],
  ["listed", "Listed"],
  ["ended", "Listing ended"],
  ["archived", "Archived"],
  ["reserved", "Reserved"],
  ["unpaid", "Unpaid order"],
  ["paid", "Paid"],
  ["packed", "Packed"],
  ["shipped", "Shipped"],
  ["completed", "Completed sale"],
  ["cancelled", "Cancelled order"],
  ["refunded", "Fully refunded"],
  ["partial_refund", "Partly refunded"],
  ["returned", "Returned"],
  ["exchanged", "Exchanged"],
  ["lost", "Lost"],
  ["damaged", "Damaged"],
  ["donated", "Donated"],
  ["personal_use", "Personal use"],
  ["written_off", "Written off"],
];
export interface MigrationAlias {
  source: string;
  key: string;
}
export interface ImportedLifecycleEvent {
  id: string;
  source: string;
  recordKey: string;
  kind: MigrationStatus;
  quantity: number;
  occurredAt: string;
  saleID?: string;
  amount?: Money;
  notes?: string;
  stockEffect: "included_in_opening_balance";
  importedAt: string;
}
export interface MigrationSheet {
  name: string;
  headers: string[];
  mapping: MigrationField[];
  defaultStatus: MigrationStatus;
}
export interface MigrationOptions {
  currency: string;
  dateOrder: SpreadsheetOptions["dateOrder"];
  numberStyle: SpreadsheetOptions["numberStyle"];
  costBasis: "unit" | "lot";
  stockAsOf: string;
  source: string;
  formulaValuesConfirmed?: boolean;
}
export interface MigrationSourceRow {
  sheet: number;
  row: number;
  cells: string[];
  warnings?: SpreadsheetSheet["cellWarnings"];
  formulaColumns?: number[];
}
export interface MigrationDecision {
  itemID?: string;
  createItem?: boolean;
  saleID?: string;
  createSale?: boolean;
  skip?: boolean;
  replaceFields?: string[];
  confirmStock?: boolean;
  confirmIdentity?: boolean;
  values?: Partial<Record<MigrationField, string>>;
}
export interface MigrationRecord extends MigrationSourceRow {
  id: string;
  workspaceID: string;
  jobID: string;
  decision: MigrationDecision;
  receipt?: {
    itemID: string;
    listingID?: string;
    saleID?: string;
    eventID?: string;
    outcome: "added" | "updated" | "unchanged";
    at: string;
    changes?: MigrationChange[];
  };
  error?: string;
}
export interface MigrationJob {
  id: string;
  workspaceID: string;
  createdBy: string;
  fileName: string;
  fingerprint: string;
  expectedRows: number;
  sheets: MigrationSheet[];
  options: MigrationOptions;
  revision: number;
  status: "uploading" | "review" | "importing" | "complete";
  createdAt: string;
  updatedAt: string;
}
export interface MigrationValues {
  recordKey: string;
  saleKey?: string;
  itemKey?: string;
  title?: string;
  sku?: string;
  itemID?: string;
  status: MigrationStatus;
  description?: string;
  condition?: ItemCondition;
  brand?: string;
  size?: string;
  category?: string;
  location?: string;
  notes?: string;
  available?: number;
  quantity: number;
  quantitySupplied?: boolean;
  purchaseCost?: number;
  acquiredAt?: string;
  targetPrice?: number;
  marketplace?: Marketplace;
  seller?: string;
  listingID?: string;
  listingURL?: string;
  listingStatus?: ListingStatus;
  listingPrice?: number;
  listedAt?: string;
  orderID?: string;
  lineID?: string;
  soldAt?: string;
  salePrice?: number;
  shippingCharged?: number;
  fees?: number;
  paymentFees?: number;
  shippingCost?: number;
  packagingCost?: number;
  refund?: number;
  eventAt?: string;
  carrier?: string;
  tracking?: string;
  shippedAt?: string;
  completedAt?: string;
}
export interface MigrationChange {
  field: string;
  label: string;
  before: string;
  after: string;
  conflict: boolean;
  selected: boolean;
}
export interface MigrationReviewRow {
  sourceValues: Partial<Record<MigrationField, string>>;
  version: string;
  id: string;
  sheet: string;
  row: number;
  title: string;
  sku?: string;
  status: "ready" | "attention" | "saved" | "skipped" | "unchanged";
  itemID?: string;
  itemTitle?: string;
  group: string;
  issues: string[];
  suggestions: Array<
    Pick<
      InventoryItem,
      "id" | "title" | "sku" | "condition" | "quantity" | "status" | "size"
    >
  >;
  saleID?: string;
  saleSuggestions: Array<
    Pick<
      Sale,
      | "id"
      | "marketplace"
      | "quantity"
      | "soldAt"
      | "salePrice"
      | "status"
      | "externalOrderID"
      | "externalOrderLineItemID"
    > & { label: string }
  >;
  changes: MigrationChange[];
  decision: MigrationDecision;
  values?: MigrationValues;
  outcome?: string;
  receipt?: MigrationRecord["receipt"];
}
export interface MigrationPage {
  job: MigrationJob;
  rows: MigrationReviewRow[];
  received: number;
  counts: {
    ready: number;
    attention: number;
    saved: number;
    skipped: number;
    unchanged: number;
    newItems: number;
    updatedItems: number;
    listings: number;
    sales: number;
    events: number;
    stockChanges: number;
  };
  reviewToken: string;
}
export type MigrationRequest =
  | { action: "list" }
  | {
      action: "create";
      job: Pick<
        MigrationJob,
        | "id"
        | "fileName"
        | "fingerprint"
        | "expectedRows"
        | "sheets"
        | "options"
      >;
    }
  | { action: "search"; query: string }
  | { action: "upload"; jobID: string; rows: MigrationSourceRow[] }
  | { action: "read"; jobID: string }
  | {
      action: "decide";
      jobID: string;
      revision: number;
      decisions: Array<{ id: string; decision: MigrationDecision }>;
    }
  | {
      action: "commit";
      jobID: string;
      rows: Array<{ id: string; version: string }>;
    }
  | { action: "delete"; jobID: string };
export const migrationSaleStatuses: Partial<
  Record<MigrationStatus, SaleStatus>
> = {
  paid: "paid",
  packed: "packed",
  shipped: "shipped",
  completed: "completed",
  cancelled: "cancelled",
  refunded: "refunded",
  partial_refund: "completed",
  returned: "completed",
  exchanged: "completed",
};

const headerAliases: Partial<Record<MigrationField, string[]>> = {
  record_id: ["record id", "transaction id", "reference"],
  item_id: ["item id", "listwork item id"],
  sku: ["sku", "stock code", "stock number", "product code", "custom label"],
  title: ["title", "item", "item title", "item name", "product"],
  description: ["description", "listing description"],
  status: ["status", "state", "item status", "order status"],
  condition: ["condition"],
  brand: ["brand", "maker"],
  size: ["size", "variant"],
  category: ["category"],
  location: ["location", "stock location", "box", "shelf"],
  notes: ["notes"],
  available: [
    "available",
    "current stock",
    "quantity available",
    "available quantity",
    "remaining",
    "in stock",
  ],
  quantity: ["quantity", "qty", "quantity sold", "units sold"],
  purchase_cost: [
    "cost",
    "purchase cost",
    "cost price",
    "purchase price",
    "unit cost",
    "cost per unit",
    "lot cost",
  ],
  cost_units: ["cost units", "lot quantity", "units in lot"],
  acquired_at: ["date bought", "purchase date", "acquired date"],
  target_price: ["target price", "target"],
  currency: ["currency"],
  marketplace: ["marketplace", "channel", "platform", "sold on"],
  seller: ["seller", "seller account", "seller account id"],
  listing_id: [
    "listing id",
    "ebay item id",
    "ebay item number",
    "listing number",
  ],
  listing_url: ["listing url", "url", "listing link"],
  listing_status: ["listing status"],
  listing_price: ["listing price", "asking price"],
  listed_at: ["date listed", "listed at"],
  order_id: ["order id", "order number"],
  line_id: ["order line id", "line id"],
  sold_at: ["sale date", "sold at", "date sold"],
  sale_price: [
    "sale price",
    "sold price",
    "sale amount",
    "sold for",
    "revenue",
  ],
  shipping_charged: ["shipping charged", "postage charged", "buyer postage"],
  fees: ["fees", "marketplace fees", "ebay fees"],
  payment_fees: ["payment fees", "processing fees"],
  shipping_cost: ["shipping cost", "postage cost"],
  packaging_cost: ["packaging cost", "packaging"],
  refund: ["refund", "refund amount"],
  event_at: ["event date", "return date", "refund date"],
  carrier: ["carrier"],
  tracking: ["tracking", "tracking number"],
  shipped_at: ["date shipped", "shipped at"],
  completed_at: ["date completed", "completed at"],
};
const headerKey = (value: string) =>
  value
    .toLowerCase()
    .replace(/[_\-]/g, " ")
    .replace(/[£$€]/g, "")
    .replace(/\s+/g, " ")
    .trim();
export function suggestMigrationMapping(headers: string[]): MigrationField[] {
  const used = new Set<MigrationField>();
  return headers.map((label) => {
    const field =
      migrationFields.find(
        ([key]) =>
          key !== "ignore" &&
          [key, ...(headerAliases[key] ?? [])].some(
            (alias) => headerKey(alias) === headerKey(label),
          ),
      )?.[0] ?? "ignore";
    if (used.has(field)) return "ignore";
    used.add(field);
    return field;
  });
}
export function suggestMigrationHeader(rows: string[][]) {
  let best = 0,
    score = 0;
  rows.slice(0, 20).forEach((row, index) => {
    const next = suggestMigrationMapping(row).filter(
      (field) => field !== "ignore",
    ).length;
    if (next > score) {
      best = index;
      score = next;
    }
  });
  return best;
}
export function migrationReportCSV(page: MigrationPage): string {
  const cell = (value: unknown) =>
    '"' +
    String(value ?? "")
      .replace(/^[=+@\-\t\r]/, "'$&")
      .replace(/"/g, '""') +
    '"';
  return [
    [
      "Sheet",
      "Row",
      "Item",
      "SKU",
      "Outcome",
      "Listwork item ID",
      "Sale ID",
      "Needs review",
      "Mapped source values",
      "Corrections",
      "Reviewed changes",
    ],
    ...page.rows.map((row) => [
      row.sheet,
      row.row,
      row.title,
      row.sku,
      row.receipt?.outcome ?? row.status,
      row.receipt?.itemID ?? row.itemID,
      row.receipt?.saleID,
      row.issues.join("; "),
      JSON.stringify(row.sourceValues),
      JSON.stringify(row.decision.values ?? {}),
      JSON.stringify(row.receipt?.changes ?? row.changes),
    ]),
  ]
    .map((row) => row.map(cell).join(","))
    .join("\r\n");
}
